// Helpers for safely reading the currently-executing <script> tag's URL.
//
// WHY THIS EXISTS: consent managers (notably Termly Auto Blocker) install
// overrides that make `document.currentScript.src` return a RELATIVE value
// (e.g. "/web-vitals-monitor.js") instead of the resolved absolute URL. A naive
// `new URL(document.currentScript.src)` then throws "Invalid URL" and crashes
// SDK initialisation before Sentry loads. See docs/KNOWN_ISSUES.md.
//
// ALWAYS use these helpers instead of touching `document.currentScript.src`
// directly. Note: `document.currentScript` is only populated during the
// script's synchronous top-level execution — call these at module load time,
// not from later async callbacks (where it is null).

/**
 * Resolve the executing script's `src` to an absolute URL, tolerating both
 * absolute and relative values (relative ones are resolved against the page
 * URL). Returns `null` when there is no usable current script or the value
 * cannot be parsed — never throws.
 */
export function getCurrentScriptUrl(): URL | null {
    const currentScript = typeof document !== 'undefined' ? document.currentScript : null;
    const base = typeof window !== 'undefined' ? window.location.href : undefined;

    if (currentScript instanceof HTMLScriptElement && currentScript.src) {
        try {
            return new URL(currentScript.src, base);
        } catch {
            return null;
        }
    }

    return null;
}

/**
 * Origin of the executing script, falling back to the page origin when the
 * script URL is unavailable. Never throws.
 */
export function getCurrentScriptOrigin(): string {
    const fallback = typeof window !== 'undefined' ? window.location.origin : '';
    return getCurrentScriptUrl()?.origin ?? fallback;
}

/**
 * Read an attribute (e.g. `data-site-id`) from the executing <script> tag.
 * Returns the trimmed value, or `null` when there is no current script or the
 * attribute is absent/empty. Like the helpers above, call this at module load
 * time — `document.currentScript` is null in later async callbacks.
 */
export function getCurrentScriptAttribute(name: string): string | null {
    const currentScript = typeof document !== 'undefined' ? document.currentScript : null;

    if (currentScript instanceof HTMLScriptElement) {
        const value = currentScript.getAttribute(name);
        return value && value.trim() ? value.trim() : null;
    }

    return null;
}
